import React, { useMemo, useCallback } from "react";
import { DayPicker, Modifiers } from "react-day-picker";
import "react-day-picker/style.css";

interface ScheduleCalendarProps {
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  activityByDay: Record<string, number>;
  platform?: "instagram" | "facebook";
}

type ActivityLevel = "high" | "medium" | "low" | "none";

function getDayOfWeek(date: Date): string {
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  return days[date.getDay()];
}

/**
 * Classify days by rank instead of normalization.
 * This handles cases where all values are similar (e.g., 20741-21535).
 * Returns a map of day -> activity level based on ranking.
 */
function classifyByRank(activityByDay: Record<string, number>): Map<string, ActivityLevel> {
  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const result = new Map<string, ActivityLevel>();

  // Get day-value pairs and sort by activity descending
  const ranked = days
    .map(day => ({ day, value: activityByDay[day] || 0 }))
    .sort((a, b) => b.value - a.value);

  // Check if we have any data
  const hasData = ranked.some(r => r.value > 0);
  if (!hasData) {
    days.forEach(day => result.set(day, "none"));
    return result;
  }

  // Classify by rank: top 2 = high, middle 3 = medium, bottom 2 = low
  ranked.forEach((item, idx) => {
    if (idx < 2) {
      result.set(item.day, "high");
    } else if (idx < 5) {
      result.set(item.day, "medium");
    } else {
      result.set(item.day, "low");
    }
  });

  return result;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  selectedDate,
  onSelectDate,
  activityByDay,
}) => {
  const activityLevels = useMemo(() => {
    return classifyByRank(activityByDay);
  }, [activityByDay]);

  const modifiers = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const highActivityDays: Date[] = [];
    const mediumActivityDays: Date[] = [];
    const lowActivityDays: Date[] = [];

    // Generate dates for the next 3 months
    for (let i = 0; i < 90; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);

      const dayName = getDayOfWeek(date);
      const level = activityLevels.get(dayName) || "none";

      switch (level) {
        case "high":
          highActivityDays.push(new Date(date));
          break;
        case "medium":
          mediumActivityDays.push(new Date(date));
          break;
        case "low":
          lowActivityDays.push(new Date(date));
          break;
      }
    }

    return {
      highActivity: highActivityDays,
      mediumActivity: mediumActivityDays,
      lowActivity: lowActivityDays,
    };
  }, [activityLevels]);

  const modifiersClassNames = {
    highActivity: "rdp-day-high-activity",
    mediumActivity: "rdp-day-medium-activity",
    lowActivity: "rdp-day-low-activity",
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const handleSelect = useCallback((
    selected: Date | undefined,
    _triggerDate: Date,
    _modifiers: Modifiers,
    _e: React.MouseEvent | React.KeyboardEvent
  ) => {
    onSelectDate(selected);
  }, [onSelectDate]);

  return (
    <div className="schedule-calendar">
      <style>{`
        .schedule-calendar .rdp {
          --rdp-accent-color: #2563eb;
          --rdp-background-color: #eff6ff;
        }

        .schedule-calendar .rdp-day-high-activity:not(.rdp-selected) .rdp-day_button {
          background-color: #22c55e;
          color: white;
          border-radius: 9999px;
        }

        .schedule-calendar .rdp-day-medium-activity:not(.rdp-selected) .rdp-day_button {
          background-color: #facc15;
          color: #1f2937;
          border-radius: 9999px;
        }

        .schedule-calendar .rdp-day-low-activity:not(.rdp-selected) .rdp-day_button {
          background-color: #f87171;
          color: white;
          border-radius: 9999px;
        }

        .schedule-calendar .rdp-selected .rdp-day_button {
          background-color: #2563eb !important;
          color: white !important;
          border-radius: 9999px;
        }

        .schedule-calendar .rdp-day_button:hover {
          opacity: 0.8;
        }
      `}</style>

      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={handleSelect}
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        disabled={{ before: today }}
        numberOfMonths={1}
        showOutsideDays={false}
      />

      <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
          <span>Best</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-yellow-400" />
          <span>Good</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-red-400" />
          <span>Low</span>
        </div>
      </div>
    </div>
  );
};
